import { useState, useEffect, useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import { EuiTitle, EuiProgress, EuiIcon } from '@equipmentshare/ds2'
import { useNavigate, useParams, Link } from 'react-router-dom'
import ItemHeaderDetails from './components/ItemHeaderDetails'
import ItemDisplay from './components/ItemDisplay'
import EditStorePart from './components/edit-store-part/edit-store-part'
import PermissionsCheck from 'components/PermissionsCheck'
import getConvertPartsModal from './components/modals/convert-parts-modal'
import getAdjustQuantityModal from './components/modals/adjust-quantity-modal'
import getOverrideWacModal from './components/modals/override-wac-modal'
import getDeleteItemModal from './components/modals/remove-from-inventory-modal'
import LinearProgress from '@mui/material/LinearProgress'
import { useFlags } from '@equipmentshare/feature-flags'
import { Permission, useStorePartDetailsQuery } from '../../graphql'
import { useDispatchSetSelectedModal } from '../../contexts/modal-context/modal-context'
import { heapEvent } from '@equipmentshare/heaper'

import EditMenu from './components/EditMenu'

import styles from './index.module.css'
import ConvertPartFlyout from './components/flyouts/convert-part-flyout'
import ItemDetailRedesign from './redesign/ItemDetailRedesign'

export default function StorePart( ) {
	const navigate = useNavigate()
	const { id } = useParams()

	const flags = useFlags()
	// No `??` fallback on purpose: an unevaluated flag renders the shipped legacy layout.
	const newLayout = flags.costentoryEnableItemDetailsV2
	// Second gate so the redesigned layout can ship ahead of the history panel. Read here
	// rather than in the child because the refetch below has to know whether the panel —
	// and therefore the `ItemHistory` query — is actually mounted.
	const showTransactionHistory = Boolean( newLayout && flags.costentoryEnableItemDetailsV2TransactionHistory )

	const setSelectedModal = useDispatchSetSelectedModal()
	const client = useApolloClient()
	const { data, loading, refetch } = useStorePartDetailsQuery( { variables: { storePartId: id } } )
	const [ isEditMode, setIsEditMode ] = useState( false )
	const item = data?.storePart

	const [ isConvertPartFlyoutVisible, setIsConvertPartFlyoutVisible ] = useState( false )

	useEffect( () => {
		if ( !id ) {
			navigate( -1 )
		}
	}, [ id ] )

	/**
	 * Refresh everything an action on this page can change. `refetch` covers the quantities
	 * in the header and state panel; the Transaction History table is a separate
	 * `ItemHistory` query owned further down the tree, so actions that write a ledger entry
	 * have to invalidate it explicitly or the table stays stale until a page reload.
	 *
	 * Guarded on the panel being mounted: Apollo logs `Unknown query named "ItemHistory"`
	 * when nothing active matches the name.
	 */
	const refetchItemAndHistory = useCallback( async() => {
		await refetch()
		if ( showTransactionHistory ) {
			await client.refetchQueries( { include: [ 'ItemHistory' ] } )
		}
	}, [ refetch, client, showTransactionHistory ] )

	const onEditClick = () => {
		heapEvent( 'Inventory - Item Details - Click - Edit Details', {} )
		setIsEditMode( true )
	}

	const onEditCancel = () => {
		heapEvent( 'Inventory - Edit Item Details - Click - Cancel', {} )
		setIsEditMode( false )
	}

	const onEditSubmit = () => {
		heapEvent( 'Inventory - Edit Item Details - Click - Submit', {} )
	}

	const onConvertItemsClick = () => {
		heapEvent( 'Inventory - Item Details - Click - Convert Items', {} )
		setSelectedModal( getConvertPartsModal( item, refetchItemAndHistory ) )
	}

	const onConvertItemsToManyClick = () => {
		heapEvent( 'Inventory - Item Details - Click - Convert Items', {} )
		setIsConvertPartFlyoutVisible( true )
	}

	const onAdjustQuantityClick = () => {
		heapEvent( 'Inventory - Item Details - Click - Adjust Quantity', {} )
		if ( item ) {
			setSelectedModal( getAdjustQuantityModal( item.id, refetchItemAndHistory ) )
		}
	}

	const onOverrideWacClick = () => {
		heapEvent( 'Inventory - Item Details - Click - Edit WAC', {} )
		setSelectedModal( getOverrideWacModal( item, refetch ) )
	}

	const onDeleteItem = () => {
		heapEvent( 'Inventory - Item Details - Click - Delete Item', {} )
		setSelectedModal( getDeleteItemModal( item, refetch ) )
	}

	if ( newLayout && !isEditMode ) {
		return (
			<>
				{ loading && <LinearProgress /> }
				{ data?.storePart && item?.part && (
					<ItemDetailRedesign
						item={item}
						showTransactionHistory={showTransactionHistory}
						onEdit={onEditClick}
						onAdjustQuantity={onAdjustQuantityClick}
						onConvertItems={onConvertItemsClick}
						onConvertItemsToMany={onConvertItemsToManyClick}
						onOverrideWac={onOverrideWacClick}
						onDeleteItem={onDeleteItem}
					/>
				) }
				{ isConvertPartFlyoutVisible && data?.storePart && item?.part &&
					<ConvertPartFlyout storePart={item} onClose={() => setIsConvertPartFlyoutVisible( false )} onSuccess={refetchItemAndHistory} /> }
			</>
		)
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<Link to="/">
					<EuiIcon type="arrowLeft" />
          			Back to Inventory
				</Link>
				{ !isEditMode && item && (
					<PermissionsCheck permissionsToCheck={[ Permission.CanEditStorePartsInfoByStoreId ]}>
						<EditMenu
							onEdit={onEditClick}
							onConvertItems={onConvertItemsClick}
							onConvertItemsToMany={onConvertItemsToManyClick}
							onAdjustQuantity={onAdjustQuantityClick}
							onOverrideWac={onOverrideWacClick}
							onDeleteItem={onDeleteItem}
							className={styles.alignRight}
						/>
					</PermissionsCheck>
				) }
			</div>
			<div className={styles.content}>
				{ loading && <EuiProgress size="xs" />}
				{ data?.storePart && item?.part && (
					<>
						<EuiTitle size="l"><h1>{item.part.partNumber}</h1></EuiTitle>
						<ItemHeaderDetails
							item={item}
							hideLocation={isEditMode}
						/>

						{ isEditMode ? (
							<EditStorePart
								storePart={item}
								onClose={onEditCancel}
								onSubmit={onEditSubmit}
								onSuccess={() => setIsEditMode( false )}
							/>
						) : (
							<ItemDisplay item={item} />
						) }
					</>
				) }
			</div>
			{
				isConvertPartFlyoutVisible && data?.storePart && item?.part &&
        <ConvertPartFlyout storePart={item} onClose={() => setIsConvertPartFlyoutVisible( false )} onSuccess={refetchItemAndHistory} />
			}
		</div>
	)
}
